import { useBundle } from "@amzn/react-arb-tools";
import { useCallback } from "react";

export interface ArbBundle {
  getMessage: (value: string) => string;
  formatMessage: (value: string, ...args: any) => string;
}

/**
 * A wrapper around the useBundle hook from react-arb-tools.
 *
 * Handles unloaded behavior by returning empty string rather than the message.
 * Logs errors in getting translations to console, then returns the key to
 * prevent the entire application from failing if a key isn't found.
 *
 * @param bundleName Name of the the ARB Bundle to be used.
 * @returns object with getMessage and formatMessage functions, using the same
 * 	signatures as useBundle.
 */
export default function useArb(bundleName: string): ArbBundle {
  const [bundle, isLoading] = useBundle(bundleName);

  const getMessage = useCallback(
    (value: string) => {
      if (isLoading) {
        return "";
      } else {
        try {
          return bundle.getMessage(value);
        } catch (e) {
          console.error(e);
          return "N/A";
        }
      }
    },
    [bundle, isLoading]
  );

  const formatMessage = useCallback(
    (value: string, ...args: any) => {
      if (isLoading) {
        return "";
      } else {
        try {
          return bundle.formatMessage(value, ...args);
        } catch (e) {
          console.error(e);
          return "N/A";
        }
      }
    },
    [bundle, isLoading]
  );

  return {
    getMessage,
    formatMessage,
  };
}
